/**
 * Today's Visit Scheduler
 *
 * Shows today's care pattern and options for scheduling visits.
 * Includes admission visit and option to auto-schedule remaining visits.
 */

import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { formatInTimeZone } from 'date-fns-tz';
import { isSameDay } from 'date-fns';

export interface TodaysVisitSchedulerProps {
  admissionTime: string;
  carePatternName: string;
  visitWindows: Array<{
    label: string;
    earliestStart: string;
    latestStart: string;
  }>;
  autoScheduleToday: boolean;
  onAutoScheduleChange: (checked: boolean) => void;
  disabled?: boolean;
  patientTimezone: string;
}

export function TodaysVisitScheduler({
  admissionTime,
  carePatternName,
  visitWindows,
  autoScheduleToday,
  onAutoScheduleChange,
  disabled = false,
  patientTimezone,
}: TodaysVisitSchedulerProps) {
  // Format and compare times in the patient's timezone
  const admissionTimeFormatted = formatInTimeZone(new Date(admissionTime), patientTimezone, 'h:mm a zzz');
  const admissionHourInPatientTz = parseInt(formatInTimeZone(new Date(admissionTime), patientTimezone, 'H'), 10);

  // "today" vs "tomorrow" label based on whether the admission date is today
  const admissionDateLabel = isSameDay(new Date(admissionTime), new Date()) ? 'today' : 'tomorrow';

  const remainingWindows = visitWindows.filter((window) => {
    const [windowHour] = window.earliestStart.split(':').map(Number);
    return windowHour > admissionHourInPatientTz;
  });

  return (
    <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Alert severity="info" icon={<AccessTimeIcon />} sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Care Pattern:</strong> {carePatternName}
        </Typography>
      </Alert>

      <Typography variant="subtitle2" gutterBottom>
        Visits to be scheduled:
      </Typography>

      <List dense>
        <ListItem>
          <ListItemIcon>
            <CheckCircleOutlineIcon color="success" />
          </ListItemIcon>
          <ListItemText
            primary="Admission Visit"
            secondary={`${admissionTimeFormatted} (90 minutes)`}
          />
        </ListItem>

        {remainingWindows.map((window, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              <AccessTimeIcon color={autoScheduleToday ? 'success' : 'action'} />
            </ListItemIcon>
            <ListItemText
              primary={window.label}
              secondary={`${window.earliestStart} - ${window.latestStart} window`}
            />
          </ListItem>
        ))}
      </List>

      {remainingWindows.length > 0 && (
        <FormControlLabel
          control={
            <Checkbox
              checked={autoScheduleToday}
              onChange={(e) => onAutoScheduleChange(e.target.checked)}
              disabled={disabled}
            />
          }
          label={
            <Typography variant="body2">
              Auto-schedule {remainingWindows.length} additional visit
              {remainingWindows.length !== 1 ? 's' : ''} for {admissionDateLabel}
            </Typography>
          }
        />
      )}

      {autoScheduleToday && remainingWindows.length > 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            {remainingWindows.length + 1} total visit{remainingWindows.length + 1 !== 1 ? 's' : ''} will
            be created for {admissionDateLabel}
          </Typography>
        </Alert>
      )}
    </Box>
  );
}
